import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { usePreferences } from '../contexts/PreferencesContext';

interface UserPreferencesProps {
  open: boolean;
  onClose: () => void;
}

const UserPreferences: React.FC<UserPreferencesProps> = ({ open, onClose }) => {
  const { preferences, updatePreferences, loading, error } = usePreferences();

  const [localPreferences, setLocalPreferences] = useState(() => ({
    language: preferences.language,
    dailyCalorieTarget: preferences.dailyCalorieTarget.toString()
  }));

  // Update local preferences when the main preferences change
  React.useEffect(() => {
    console.log('Preferences updated:', preferences);
    setLocalPreferences({
      language: preferences.language,
      dailyCalorieTarget: preferences.dailyCalorieTarget.toString()
    });
  }, [preferences]);

  const handleLanguageChange = (newLanguage: 'en' | 'fr' | 'es') => {
    setLocalPreferences(prev => ({
      ...prev,
      language: newLanguage
    }));
  };

  const handleCalorieTargetChange = (newTarget: string) => {
    setLocalPreferences(prev => ({
      ...prev,
      dailyCalorieTarget: newTarget
    }));
  };

  const handleSave = async () => {
    console.log('Saving preferences...');
    const calories = parseInt(localPreferences.dailyCalorieTarget, 10);
    if (!isNaN(calories) && calories >= 500 && calories <= 10000) {
      try {
        await updatePreferences({
          language: localPreferences.language,
          dailyCalorieTarget: calories
        });
        onClose();
      } catch (err) {
        console.error('Failed to update preferences:', err);
      }
    } else {
      setLocalPreferences(prev => ({
        ...prev,
        dailyCalorieTarget: preferences.dailyCalorieTarget.toString()
      }));
    }
  };

  const handleClose = () => {
    // Reset local state to current preferences
    setLocalPreferences({
      language: preferences.language,
      dailyCalorieTarget: preferences.dailyCalorieTarget.toString()
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>User Preferences</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
            <CircularProgress />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '10px' }}>
            <FormControl fullWidth>
              <InputLabel>Language</InputLabel>
              <Select
                value={localPreferences.language}
                label="Language"
                onChange={(e) => handleLanguageChange(e.target.value as 'en' | 'fr' | 'es')}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="fr">Français</MenuItem>
                <MenuItem value="es">Español</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <TextField
                label="Daily Calorie Target"
                type="number"
                value={localPreferences.dailyCalorieTarget}
                onChange={(e) => handleCalorieTargetChange(e.target.value)}
                inputProps={{
                  min: 500,
                  max: 10000,
                  step: 50
                }}
                helperText="Set your daily calorie target (500-10000)"
              />
            </FormControl>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserPreferences;
